import { UndoIcon } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute, formatMicros } from '@/lib/format'

import {
  approveRefundRequest,
  rejectRefundRequest,
  useStaffMutation,
  useStaffRefundRequests,
  type StaffRefundRequest,
} from '../api'
import { useStaff } from '../hooks'
import { RefundResultPanel } from '../refunds'
import { SELECT_CLASS } from '../shared'
import { ActionNotice, ReasonDialog, ShortId, StaffGate, StaffLink } from '../ui'

type Pending = { row: StaffRefundRequest; kind: 'approve' | 'reject' }

/**
 * The refund-request queue (T5): what admins asked for, oldest first. A superoperator approves (the
 * refund runs at once: a credit note per top-up, then the card) or rejects, always with a reason.
 */
export function StaffRefundsPage() {
  const staff = useStaff()
  const [state, setState] = useState('REQUESTED')
  const list = useStaffRefundRequests(state)
  const rows = list.data ?? []
  const [pending, setPending] = useState<Pending | null>(null)
  const [notice, setNotice] = useState<{ title: string; notified: boolean | undefined; detail?: ReactNode } | null>(null)
  const canDecide = staff.can('billing_plan_quota')
  const approve = useStaffMutation(({ id, reason }: { id: string; reason: string }) => approveRefundRequest(id, reason))
  const reject = useStaffMutation(({ id, reason }: { id: string; reason: string }) => rejectRefundRequest(id, reason))

  return (
    <StaffGate>
      <PageHeader eyebrow="Staff" title="Refund requests" description="Refunds that account administrators asked for, waiting for a decision." />
      <PageBody>
        <ActionNotice notice={notice} onDismiss={() => { setNotice(null) }} />
        <Card>
          <CardContent className="space-y-4">
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="refund-state">State</Label>
              <select id="refund-state" className={SELECT_CLASS} value={state} onChange={(e) => { setState(e.target.value) }}>
                <option value="REQUESTED">Waiting for a decision</option>
                <option value="PENDING_MANUAL">Approved, paid out by hand</option>
                <option value="DONE">Refunded</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            {list.isError ? (
              <ErrorState error={list.error} title="Could not load refund requests" onRetry={() => void list.refetch()} />
            ) : list.isPending ? (
              <TableSkeleton rows={3} columns={6} />
            ) : rows.length === 0 ? (
              <EmptyState icon={UndoIcon} title="No refund requests" description="Nothing is in this state." />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Reason given</TableHead>
                      <TableHead>Requested by</TableHead>
                      <TableHead>Requested</TableHead>
                      {state === 'REQUESTED' ? null : <TableHead>Decision</TableHead>}
                      {canDecide && state === 'REQUESTED' ? <TableHead /> : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id} data-testid="staff-refund-request-row" data-account={r.account_id}>
                        <TableCell><StaffLink to={`/app/staff/accounts/${r.account_id}`}>{r.account_id.slice(0, 8)}</StaffLink></TableCell>
                        <TableCell className="text-right tabular-nums">{formatMicros(r.amount_micros)}</TableCell>
                        <TableCell className="max-w-[22rem] whitespace-normal text-sm">{r.reason ?? '—'}</TableCell>
                        <TableCell><StaffLinkOrId id={r.requested_by} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground tabular-nums">{formatAbsolute(r.created_at)}</TableCell>
                        {state === 'REQUESTED' ? null : (
                          <TableCell className="max-w-[18rem] whitespace-normal text-xs text-muted-foreground">
                            {r.decision_reason ?? '—'}{r.decided_at === null ? '' : ` · ${formatAbsolute(r.decided_at)}`}
                          </TableCell>
                        )}
                        {canDecide && state === 'REQUESTED' ? (
                          <TableCell>
                            <div className="flex justify-end gap-2">
                              <Button size="sm" onClick={() => { setPending({ row: r, kind: 'approve' }) }}>Approve</Button>
                              <Button size="sm" variant="outline" onClick={() => { setPending({ row: r, kind: 'reject' }) }}>Reject</Button>
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageBody>

      {pending === null ? null : (
        <ReasonDialog
          title={pending.kind === 'approve' ? `Approve a refund of ${formatMicros(pending.row.amount_micros)}?` : 'Reject this refund request?'}
          description={
            pending.kind === 'approve'
              ? 'The refund runs now: a Stripe credit note per top-up, newest first, tax included, and a card refund. Stripe’s fee is ours. The account’s administrators are emailed with your reason.'
              : 'The amount is released and nothing is refunded. The account’s administrators are emailed with your reason.'
          }
          confirmLabel={pending.kind === 'approve' ? 'Approve and refund' : 'Reject request'}
          destructive={pending.kind === 'reject'}
          testId="refund-decision-dialog"
          onClose={() => { setPending(null) }}
          onConfirm={async (reason) => {
            if (pending.kind === 'approve') {
              const r = await approve.mutateAsync({ id: pending.row.id, reason })
              setNotice({
                title: r.refund.pending_manual ? 'Approved, card payout pending' : 'Refund approved',
                notified: r.notified,
                detail: <RefundResultPanel result={r.refund} />,
              })
            } else {
              const r = await reject.mutateAsync({ id: pending.row.id, reason })
              setNotice({ title: 'Request rejected', notified: r.notified })
            }
          }}
        />
      )}
    </StaffGate>
  )
}

function StaffLinkOrId({ id }: { id: string }) {
  return <StaffLink to={`/app/staff/users/${id}`}><ShortId id={id} /></StaffLink>
}
